// ModalAvaliacao.js — Pop-up de edição de uma avaliação, na aba "Avaliações".
//
// A estrutura veio do `produto/gridProduto/QuickView.js`, que JÁ é um modal com
// capa à esquerda e conteúdo à direita, com Esc para fechar, trava do scroll do
// fundo e AnimatePresence. Criar um segundo modal do zero significaria dois
// comportamentos diferentes para a mesma coisa no mesmo site.
//
// Detalhes que costumam faltar e estão aqui de propósito:
//  - "Salvar" fica desabilitado enquanto nada mudou (evita PUT à toa);
//  - a mensagem de erro é a do backend, não uma genérica;
//  - a tela avisa que editar NÃO gera novos coins — senão vira reclamação;
//  - em tela estreita o conteúdo empilha (capa em cima, formulário embaixo).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "motion/react";
import BorderGlow from "../../ui/BorderGlow";
import { COINS_POR_AVALIACAO } from "../../../config/coins";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const fmtData = (iso) => {
  if (!iso) return "";
  const d = new Date(typeof iso === "string" ? iso.replace(" ", "T") : iso);
  return isNaN(d) ? "" : d.toLocaleDateString("pt-BR");
};

// Estrelas clicáveis — mesmo desenho do formulário da página do livro.
const StarInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const ativo = hover || value;
  return (
    <div className="inline-flex items-center gap-1 text-2xl text-insigne-orange" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
          className="hover:scale-110 transition-transform"
        >
          <i className={"bi " + (ativo >= n ? "bi-star-fill" : "bi-star")} />
        </button>
      ))}
    </div>
  );
};

const ModalAvaliacao = ({ avaliacao, onFechar, onSalvar, salvando }) => {
  const reduzirMovimento = useReducedMotion();

  const [nota, setNota] = useState(avaliacao?.nota || 0);
  const [comentario, setComentario] = useState(avaliacao?.comentario || "");
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);

  // Reabrir o modal em outra avaliação precisa recarregar os campos: sem isto
  // o formulário mostraria o texto da avaliação anterior.
  useEffect(() => {
    setNota(avaliacao?.nota || 0);
    setComentario(avaliacao?.comentario || "");
    setErro(null);
    setSalvo(false);
  }, [avaliacao]);

  // Esc fecha e o fundo não rola enquanto o modal está aberto
  useEffect(() => {
    if (!avaliacao) return;
    const aoTeclar = (e) => { if (e.key === "Escape") onFechar(); };
    document.addEventListener("keydown", aoTeclar);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [avaliacao, onFechar]);

  const mudou =
    avaliacao &&
    (nota !== avaliacao.nota || comentario.trim() !== (avaliacao.comentario || "").trim());

  const podeSalvar = mudou && nota > 0 && comentario.trim().length >= 3 && !salvando;

  const salvar = async () => {
    setErro(null);
    setSalvo(false);
    try {
      await onSalvar(avaliacao.id, { nota, comentario: comentario.trim() });
      setSalvo(true);
    } catch (e) {
      setErro(e?.message || "Não foi possível salvar a alteração.");
    }
  };

  const livro = avaliacao?.livro;

  return (
    <AnimatePresence>
      {avaliacao && (
        <m.div
          className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduzirMovimento ? 0 : 0.2 }}
          onClick={onFechar}
          role="dialog"
          aria-modal="true"
          aria-label={`Editar avaliação de ${livro?.titulo || "livro"}`}
        >
          <m.div
            className="relative w-full max-w-[720px] max-h-[88vh] overflow-y-auto"
            initial={reduzirMovimento ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduzirMovimento ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: reduzirMovimento ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <BorderGlow
              raio={4}
              className="bloco
                         shadow-[0_24px_70px_rgba(0,0,0,0.5)]"
            >
              <button
                onClick={onFechar}
                aria-label="Fechar"
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/40 border border-line
                           flex items-center justify-center text-paper hover:bg-black/70 transition-colors"
              >
                <i className="bi bi-x-lg text-sm" />
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-5 p-5 md:p-6">
                {/* Capa */}
                <Link
                  to={main + "livros/" + livro?.id}
                  className="rounded-xl overflow-hidden bg-insigne-navy-deep aspect-[3/4] max-w-[180px] mx-auto sm:mx-0 w-full block"
                >
                  {livro?.image ? (
                    <img
                      src={`${API_URL}public/${livro.image}`}
                      alt={livro.titulo}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="bi bi-book text-white/30 text-2xl" />
                    </div>
                  )}
                </Link>

                {/* Formulário */}
                <div className="min-w-0">
                  <h3 className="text-lg md:text-xl font-bold text-white leading-snug">{livro?.titulo}</h3>
                  {livro?.subtitulo && livro.subtitulo !== "null" && (
                    <p className="text-sm text-white/55 mt-1">{livro.subtitulo}</p>
                  )}

                  <div className="mt-4">
                    <StarInput value={nota} onChange={setNota} />
                  </div>

                  <textarea
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="O que você achou do livro?"
                    className="mt-3 w-full rounded-lg bg-insigne-navy-deep border border-line px-3.5 py-2.5 text-sm
                               text-white placeholder:text-white/40 outline-none focus:border-insigne-orange
                               transition-colors resize-y"
                  />

                  <p className="text-[11px] text-white/40 mt-2">
                    Avaliado em {fmtData(avaliacao.created_at)}
                    {avaliacao.updated_at && avaliacao.updated_at !== avaliacao.created_at
                      ? ` · editado em ${fmtData(avaliacao.updated_at)}`
                      : ""}
                  </p>

                  {erro && <p className="text-xs text-amber-300 mt-2">{erro}</p>}
                  {salvo && !erro && <p className="text-xs text-emerald-400 mt-2">Alteração salva.</p>}

                  <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
                    <button
                      onClick={salvar}
                      disabled={!podeSalvar}
                      className="flex-1 rounded-full bg-insigne-orange text-paper font-semibold text-sm py-2.5
                                 hover:bg-insigne-orange-dark transition-colors
                                 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-insigne-orange"
                    >
                      {salvando ? "Salvando..." : mudou ? "Salvar" : "Sem alterações"}
                    </button>
                    <Link
                      to={main + "livros/" + livro?.id}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="flex-1 text-center rounded-full border-2 border-insigne-orange text-insigne-orange
                                 font-semibold text-sm py-2.5 hover:bg-insigne-orange hover:text-paper transition-colors"
                    >
                      Ver o livro
                    </Link>
                  </div>

                  {/* Dito aqui, na hora da edição, e não numa página de ajuda:
                      é exatamente neste momento que a pessoa esperaria ganhar
                      coins de novo. */}
                  <p className="text-[11px] text-white/40 mt-3">
                    Editar não gera novos Insigne Coins: os {COINS_POR_AVALIACAO} coins são pagos
                    uma vez por livro, na primeira avaliação.
                  </p>
                </div>
              </div>
            </BorderGlow>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default ModalAvaliacao;
